import { TabType } from '@heidi/session/contracts';
import { windowIndexAtom } from '@heidi/session/data';

import { useIsSelectionAskHeidiEnabled } from '@/app-shell/flags/shell-flags';
import EmbeddingsCodesets from '@/app-shell/session-page/ScribeTabs/TabPane/EmbeddingsCodesets';
import { InlineSelectionEditController } from '@/components/tiptap/shared/inline-selection-edit';
import {
  SectionEhrProvider,
  type SectionLinkHandler,
} from '@/components/tiptap/shared/section-ehr-provider';

import { cn } from '@/lib/cn';
import { EditorType } from '@heidi/editor/core';
import { TableMenu } from '@heidi/editor/ui';
import { useIsomorphicLayoutEffect } from '@heidi/utils';
import { useQueryUserSnippets } from '@heidi/workspace/data';
import { EditorContent, type Editor } from '@tiptap/react';
import { useAtomValue } from 'jotai';
import { forwardRef, memo, useRef } from 'react';
import { AddSectionButton } from '../../shared/add-section-button';
import { InsertMenu } from '../../shared/insert-menu';
import styles from '../../shared/styles/index.module.scss';
import { isSelectionAiEnabledForEditor } from '../editor-type';
import { EditorTypeProvider } from '../editor-type-context';
import { trackSectionCreated } from '../extensions/section-node/section-analytics';
import { type SectionNodeStorage } from '../extensions/section-node/section-node';
import { getInsertCommands } from '../extensions/slash-command/group';
import { useApplyNoteEvidence } from '../hooks/useApplyNoteEvidence';
import { TextMenu } from '../menus/text-menu';

const BlockEditorMarkdown = memo(
  forwardRef<
    HTMLDivElement,
    {
      testTag: string;
      editor: Editor;
      editorType?: EditorType;
      /** NoteTab-only; Haiku also uses EditorType.NOTE but must stay enabled. */
      emphasisDisabled?: boolean;
      enableFindReplace: boolean;
      enableSnippet: boolean;
      enableBubbleMenu: boolean;
      tabType: TabType | undefined;
      isReviewPrimaryNoteOnly?: boolean;
      className?: string;
      footerComponent?: React.ReactNode;
      /**
       * Stop the editor content from growing to fill the scroller, so
       * `footerComponent` sits directly under the last line instead of at the
       * scroller's bottom edge. The note canvas needs this: its scroller ends
       * where the Ask Heidi composer begins, so a pinned footer would move
       * whenever the composer grows or shrinks.
       */
      footerFollowsContent?: boolean;
      onSectionLink?: SectionLinkHandler;
      enableInsertMenu?: boolean;
    }
  >((props, ref) => {
    const {
      testTag,
      editor,
      editorType,
      emphasisDisabled = false,
      enableFindReplace,
      enableSnippet,
      enableBubbleMenu,
      tabType,
      isReviewPrimaryNoteOnly = false,
      footerComponent,
      footerFollowsContent = false,
      className,
      onSectionLink,
      enableInsertMenu = false,
    } = props;
    useQueryUserSnippets();
    useApplyNoteEvidence(editor);
    const isSelectionAskHeidiEnabled = useIsSelectionAskHeidiEnabled();
    const isSelectionAiEnabled = isSelectionAiEnabledForEditor(
      isSelectionAskHeidiEnabled,
      editorType
    );
    const isSectionsEnabled =
      editor.extensionManager?.extensions.some((ext) => ext.name === 'sectionNode') ??
      false;

    // Stamp the editor type onto SectionNode storage so the `# ` input rule
    // (which runs outside React) can report `editor_context` on section_created.
    // Layout effect so the stamp lands before paint — i.e. before any keystroke
    // could fire the input rule — closing the first-render race.
    useIsomorphicLayoutEffect(() => {
      const storage = (
        editor.storage as unknown as
          | Record<string, SectionNodeStorage | undefined>
          | undefined
      )?.sectionNode;
      if (storage) storage.editorType = editorType;
    }, [editor, editorType]);

    const editorContentRef = useRef<HTMLDivElement>(null);
    const contentClassName = cn(
      styles.ProseMirror,
      footerFollowsContent ? 'flex flex-col' : 'flex flex-1 flex-col'
    );
    const windowIndex = useAtomValue(windowIndexAtom);

    return (
      <div
        ref={ref}
        data-testid={testTag}
        // eslint-disable-next-line tailwindcss/no-custom-classname
        className={cn(
          'prose-mirror-container scrollbar-fade relative flex size-full flex-col overflow-y-auto',
          isReviewPrimaryNoteOnly && 'opacity-50',
          className
        )}
      >
        {isSectionsEnabled ? (
          <EditorTypeProvider editorType={editorType} emphasisDisabled={emphasisDisabled}>
            <SectionEhrProvider onSectionLink={onSectionLink}>
              <EditorContent
                spellCheck={true}
                ref={editorContentRef}
                id="template-block-editor-content"
                data-testid="template-block-editor-content"
                className={contentClassName}
                editor={editor}
              />
            </SectionEhrProvider>
          </EditorTypeProvider>
        ) : (
          <EditorContent
            spellCheck={true}
            ref={editorContentRef}
            id="template-block-editor-content"
            data-testid="template-block-editor-content"
            className={contentClassName}
            editor={editor}
          />
        )}
        {editorType === EditorType.TEMPLATE &&
          (isSectionsEnabled || enableInsertMenu) && (
            <div className="flex items-center gap-2 pb-2 pt-4">
              {isSectionsEnabled && (
                <AddSectionButton
                  editor={editor}
                  onSectionCreated={() => trackSectionCreated(editorType, 'add_button')}
                />
              )}
              {enableInsertMenu && (
                <InsertMenu editor={editor} getCommands={getInsertCommands} />
              )}
            </div>
          )}
        {enableBubbleMenu && (
          <>
            <TextMenu
              editor={editor}
              editorType={editorType}
              emphasisDisabled={emphasisDisabled}
              enableFindReplace={enableFindReplace}
              enableSnippet={enableSnippet}
              enableConvertToSection={
                isSectionsEnabled &&
                (editorType === EditorType.NOTE || editorType === EditorType.TEMPLATE)
              }
              selector={`global-drag-handle-${editorType}-${windowIndex}-menu`}
            />
            <TableMenu editor={editor} />
            <InlineSelectionEditController
              editor={editor}
              enabled={isSelectionAiEnabled}
            />
          </>
        )}

        {footerComponent}
        {tabType === TabType.NOTE && <EmbeddingsCodesets />}
      </div>
    );
  })
);

BlockEditorMarkdown.displayName = 'BlockEditorMarkdown';

export default BlockEditorMarkdown;
